<script lang="ts">
	import { tick } from "svelte";
	import { flip } from "svelte/animate";
	import { fade } from "svelte/transition";
	import type { LandingPage } from "#gen/bundles/v1/bundle_pb.js";
	import GradientImage from "#lib/components/flow/viewer/GradientImage.svelte";

	interface Props {
		landingPage: LandingPage;
	}

	type Touch = Pick<PointerEvent, "pointerId" | "clientX" | "clientY">;

	let { landingPage }: Props = $props();

	let curSlide = $state(0);
	let maxSlide = $state(0);
	const ongoingTouches: Touch[] = $state([]);
	let container: HTMLDivElement | null = $state(null);

	$effect(() => {
		if (landingPage.images) {
			init();
		}
	});

	function copyTouch(touch: PointerEvent): Touch {
		return {
			pointerId: touch.pointerId,
			clientX: touch.clientX,
			clientY: touch.clientY,
		};
	}

	function ongoingTouchIndexById(idToFind: number) {
		return ongoingTouches.findIndex(({ pointerId }) => pointerId === idToFind);
	}

	async function init() {
		await tick();
		curSlide = 0;
		maxSlide = landingPage.images.length - 1;
	}

	function handleTouchStart(evt: PointerEvent) {
		// If the user touches a button, an element that can scroll horizontally,
		// or one in a container that can scroll horizontally, don't trigger the
		// swipe event
		if (evt.target instanceof HTMLButtonElement || hasHorizontalOverflow(evt.target as HTMLElement)) {
			return;
		}
		// Add the new touch record to ongoingTouches to keep track of it and
		// its initial position
		ongoingTouches.push(copyTouch(evt));
		evt.preventDefault();
	}

	function handleTouchMove(evt: PointerEvent) {
		const idx = ongoingTouchIndexById(evt.pointerId);
		if (idx === -1) {
			// This touch event is not being tracked, so ignore it
			return;
		}

		const prevTouch = ongoingTouches[idx];
		const xDiff = prevTouch.clientX - evt.clientX;
		const yDiff = prevTouch.clientY - evt.clientY;

		// Ensure the user is swiping horizontally and not vertically
		if (Math.abs(xDiff) > Math.abs(yDiff)) {
			// If the user swipes less than 50px, don't change slides
			if (Math.abs(xDiff) < 50.0) {
				return;
			}
			if (xDiff > 0) {
				nextSlide();
			} else {
				prevSlide();
			}
			// We've changed slides, so stop tracking this touch event so we
			// don't change slides multiple times for one swipe
			ongoingTouches.splice(idx, 1);
		}
	}

	function handleTouchEnd(evt: PointerEvent) {
		const idx = ongoingTouchIndexById(evt.pointerId);
		if (idx >= 0) {
			// If the touch event is still being tracked, stop tracking it
			ongoingTouches.splice(idx, 1);
		}
	}

	function nextSlide() {
		if (curSlide === maxSlide) {
			return;
		} else {
			curSlide++;
		}
	}

	function prevSlide() {
		if (curSlide === 0) {
			return;
		} else {
			curSlide--;
		}
	}

	function isSelectedDot(i: number): boolean {
		if (curSlide === 0) {
			return i === 0;
		}
		if (curSlide === maxSlide) {
			return i === Math.min(2, landingPage.images.length - 1);
		}
		return i === 1;
	}

	/**
	 * Check if the element or any of its parents, up to the container element
	 * in this component, have horizontal overflow
	 */
	function hasHorizontalOverflow(element: HTMLElement | null): boolean {
		while (element && element !== container) {
			const computedStyle = window.getComputedStyle(element);
			if (computedStyle.overflowX === "auto" || computedStyle.overflowX === "scroll") {
				return true;
			}
			element = element.parentElement;
		}
		return false;
	}
</script>

<div role="presentation" bind:this={container} onpointerdown={handleTouchStart} onpointermove={handleTouchMove} onpointerup={handleTouchEnd} onpointercancel={handleTouchEnd} class="relative mt-1! h-full w-full">
	<div class="absolute inset-0 h-full w-full overflow-hidden rounded-2xl border border-black/20">
		{#each landingPage.images as image, i (i)}
			<div class="absolute h-full w-full transition-all duration-500" style:transform="translateX({100 * (i - curSlide)}%)">
				<GradientImage priority={i === 0} lazy={i > 0} src={image.url} alt={image.altText} focalPoint={image.focalPoint} hideGradient={true} />
			</div>
		{:else}
			<GradientImage src="/carousel-1.svg" alt="No photo provided" hideGradient={true} />
		{/each}
	</div>
	<div class="absolute top-2 left-2 flex flex-none items-center">
		<div class="relative flex items-center justify-center gap-1 overflow-clip rounded-xl px-2 py-2.5">
			{#if landingPage.images.length > 1}
				<div class="absolute inset-0 bg-gray-800 blur-2xl"></div>
				{#each Array(Math.min(landingPage.images.length, 3)) as _, i (curSlide + i)}
					<div animate:flip transition:fade class="flex-none blur-none">
						{#key curSlide}
							<div class="h-{isSelectedDot(i) ? 2.5 : 2} w-{isSelectedDot(i) ? 2.5 : 2} rounded-full bg-{isSelectedDot(i) ? 'white' : 'gray-300'}"></div>
						{/key}
					</div>
				{/each}
			{/if}
		</div>
	</div>
</div>
